import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import {
  CERTIFICATIONS,
  RELOCATION_SERVICES,
  RELOCATION_REGULATIONS,
  PRIMARY_CTA,
  PRACTICES,
  CBE_BADGE,
  IMAGES,
} from "@/lib/data";

const Relocation = () => {
  const practice = PRACTICES.find((p) => p.slug === "relocation");

  return (
    <main data-testid="page-relocation">
      <PageHero
        eyebrow="Our Practice"
        title="Relocation Services"
        intro="URA, HUD 1378, RAD, and Section 8 compliant relocation for affordable and mixed-use housing, nationwide."
      />

      {/* Certifications strip */}
      <section className="bg-[#F8F9FA] border-y border-gray-200" data-testid="certifications-strip">
        <div className="container-kc py-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-2 flex justify-center md:justify-start">
              <a
                href="https://dslbd.dc.gov/page/certified-business-enterprise-cbe"
                target="_blank"
                rel="noreferrer"
                className="block transition-transform hover:scale-105"
                data-testid="cbe-badge-relocation"
                aria-label="DC Certified Business Enterprise Program"
              >
                <img src={CBE_BADGE} alt="DC Certified Business Enterprise Program" className="h-24 w-auto object-contain" />
              </a>
            </div>
            <div className="md:col-span-10">
              <p className="text-[15px] text-[#000000] font-serif italic max-w-md leading-snug mb-4">
                Black-owned, mission-driven, DC-based.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="eyebrow text-gray-500">Certified</span>
                {CERTIFICATIONS.map((c) => (
                  <span key={c.code} className="cert-chip" data-testid={`cert-${c.code.toLowerCase()}`}>{c.code}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section bg-white" data-testid="relocation-services">
        <div className="container-kc">
          <Reveal>
            <div className="max-w-3xl mb-16">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline--gold" />
                <span className="eyebrow">What We Do</span>
              </div>
              <h2 className="h-display text-3xl md:text-5xl">
                End-to-end compliant relocation, from outreach to post-move stabilization.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200">
            {RELOCATION_SERVICES.map((s, idx) => (
              <Reveal key={s.title} delay={idx * 0.04}>
                <div className="bg-white p-8 lg:p-10 h-full">
                  <div className="kc-index mb-4">0{idx + 1}</div>
                  <h3 className="font-serif text-xl md:text-2xl text-[#000000] leading-snug">{s.title}</h3>
                  <p className="text-gray-600 mt-4 text-[15px] leading-relaxed">{s.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Regulations We Master */}
      <section className="bg-[#000000] text-white" data-testid="relocation-regulations">
        <div className="container-kc py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <Reveal className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline--gold" />
                <span className="eyebrow eyebrow--gold">Regulations We Master</span>
              </div>
              <h2 className="h-display h-display--inverse text-3xl md:text-5xl">
                Deep compliance fluency across federal relocation frameworks.
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <ul className="divide-y divide-white/15 border-y border-white/15">
                {RELOCATION_REGULATIONS.map((r, idx) => (
                  <li key={r} className="py-5 flex items-center gap-6">
                    <span className="kc-index w-12">0{idx + 1}</span>
                    <span className="text-white/90 text-base md:text-lg">{r}</span>
                  </li>
                ))}
              </ul>
              <Link to={PRIMARY_CTA.to + "?practice=relocation"} className="btn btn--inverse mt-10" data-testid="relocation-cta">
                {PRIMARY_CTA.label} <span className="btn-arrow">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Featured image / proof */}
      <section className="bg-white" data-testid="relocation-proof">
        <div className="container-kc py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <Reveal className="md:col-span-7">
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img src={practice.image} alt={practice.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-5">
              <div className="eyebrow eyebrow--gold mb-4">Proof of Practice</div>
              <h3 className="font-serif text-2xl md:text-4xl text-[#000000] leading-tight">
                1,500+ households relocated. 8 multifamily projects. Up to 240 units per project.
              </h3>
              <p className="text-gray-600 mt-6 leading-relaxed">
                Federally regulated relocations across DC, Maryland, and Virginia. See the project list for owners, scopes, and unit counts.
              </p>
              <Link to="/projects" className="btn btn--secondary mt-8" data-testid="relocation-projects-link">
                View Projects <span className="btn-arrow">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* On-the-ground gallery (residential construction + community) */}
      <section className="bg-[#F8F9FA]" data-testid="relocation-gallery">
        <div className="container-kc py-20 md:py-28">
          <Reveal>
            <div className="flex items-center gap-3 mb-10">
              <span className="hairline--gold" />
              <span className="eyebrow">On the Ground</span>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-gray-200">
            {[
              { src: IMAGES.relocationConstructionA, alt: "Residential construction site" },
              { src: IMAGES.relocationConstructionB, alt: "Multifamily housing under construction" },
              { src: IMAGES.relocationCommunity,     alt: "Community engagement" },
              { src: IMAGES.relocationApartment,     alt: "Multifamily apartment exterior" },
              { src: IMAGES.relocationFamily,        alt: "Resident family in new housing" },
              { src: IMAGES.relocationMoving,        alt: "Coordinated move-in day" },
            ].map((img, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div className="bg-white aspect-[4/5] overflow-hidden">
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Relocation;
